import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Tooltip,
  Typography,
  keyframes,
} from '@mui/material';
import CancelIcon from '@mui/icons-material/Cancel';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloseIcon from '@mui/icons-material/Close';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import HubIcon from '@mui/icons-material/Hub';
import VolumeOffIcon from '@mui/icons-material/VolumeOff';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import {
  CartesianGrid,
  ComposedChart,
  Line,
  ResponsiveContainer,
  Tooltip as ChartTooltip,
  XAxis,
  YAxis,
} from 'recharts';

import {
  type StreamAnalytics,
  type StreamStats,
  type Transcoder,
  type HlsSessionStatusEnvelope,
  type PullOutputStatusEnvelope,
  type DestinationHealthEnvelope,
} from '../../services/streamsService';
import { StreamChannel } from '../../services/channelsService';
import { getMediaBoxAssets, getMediaBoxRuntime } from '../../services/mediaboxService';
import { useRuntimeAlarms, useRuntimeOutput } from '../../contexts/ConsoleContext';
import { StreamDetailsCard } from '../StreamDetailsCard';
import {
  getDestinationHealthMetrics,
  getPullHlsDisplayState,
} from '../../pages/console/destinations/DestinationsShared';
import { ChannelWorkspaceHeader } from '../channel/ChannelWorkspaceHeader';
import { formatCodecPair } from '../../utils/codecLabels';

interface Props {
  stats: StreamStats | null;
  analytics: StreamAnalytics | null;
  isStreamLive: boolean;
  user: {
    alwaysRecord?: boolean;
    streamName?: string;
    firstName?: string | null;
    lastName?: string | null;
    country?: string | null;
  } | null;
  activeChannel?: StreamChannel | null;
}

type OutputState = 'disabled' | 'connecting' | 'connected' | 'disconnected';
type TelemetryChartFilter = 'all' | 'bitrate' | 'loss' | 'latency';
type PreviewType = 'webrtc' | 'hls';
type TelemetryPoint = { time: string; bitrateKbps: number; packetLossPct: number; latencyMs: number };

const pulseAnimation = keyframes`
  0% { box-shadow: inset 4px 0 0 var(--danger), 0 0 0 0 rgba(var(--danger-rgb), 0.35); }
  70% { box-shadow: inset 4px 0 0 var(--danger), 0 0 0 7px rgba(var(--danger-rgb), 0); }
  100% { box-shadow: inset 4px 0 0 var(--danger), 0 0 0 0 rgba(var(--danger-rgb), 0); }
`;

const cardSx = { borderRadius: 0, border: '0.5px solid', borderColor: 'divider', bgcolor: 'background.paper', overflow: 'hidden' } as const;
const labelSx = { fontSize: { xs: '0.52rem', sm: '0.64rem' }, fontWeight: 800, textTransform: 'uppercase' as const, letterSpacing: '0.06em', color: 'text.disabled' } as const;
const monoSx = { fontFamily: "'DM Mono', monospace", fontSize: { xs: '0.62rem', sm: '0.76rem' }, fontWeight: 700 } as const;

const getWebRtcAudioBlockReason = (audioCodec?: string | null) => {
	const codec = String(audioCodec || '').trim().toLowerCase();
	if (codec.includes('opus')) return null;
	const label = codec && codec !== 'n/a' && codec !== '--'
		? formatCodecPair(null, audioCodec).split('/').pop()?.trim() || String(audioCodec)
		: 'not yet identified';
	return `WebRTC requires Opus audio. This source is ${label}, so HLS is selected for compatibility.`;
};

const formatBitrate = (kbps?: number | null) => {
  if (!kbps || kbps <= 0) return '--';
  return kbps >= 1000 ? `${(kbps / 1000).toFixed(2)} Mbps` : `${Math.round(kbps)} Kbps`;
};

const formatUptime = (seconds?: number | null) => {
  if (!seconds || seconds <= 0) return '--';
  const hours = Math.floor(seconds / 3600);
  const mins = Math.floor((seconds % 3600) / 60);
  const secs = Math.floor(seconds % 60);
  return [hours, mins, secs].map((part) => String(part).padStart(2, '0')).join(':');
};

const formatMs = (value?: number | null) => {
  if (value === undefined || value === null || Number.isNaN(Number(value))) return '--';
  return `${Number(value).toFixed(Number(value) >= 10 ? 0 : 1)} ms`;
};

const truncateMetricText = (value?: string | null, max = 24) => {
  const clean = String(value || '').trim();
  if (!clean) return null;
  return clean.length > max ? `${clean.slice(0, Math.max(0, max - 1))}…` : clean;
};

const outputSummaryText = (connected: number, active: number) => {
  if (active <= 0) return { value: '0 on', helper: 'No outputs on' };
  if (connected === active) return { value: `${active} on`, helper: `${connected} connected` };
  return { value: `${active} on`, helper: `${connected} connected, ${active - connected} disconnected` };
};

const currentSourceLabel = (stats: StreamStats | null) => {
  if (stats?.sourceLabel) return stats.sourceLabel;
  if (stats?.sourceMode === 'PLAYKASTR') return 'PlayKastr';
  if ((stats as any)?.isPlayKastrSource) return 'PlayKastr';
  if ((stats as any)?.isMediaBoxSource) return 'MediaBox';
  if (stats?.sourceType === 'SRT' || stats?.type === 'srtConn') return 'SRT';
  if (stats?.sourceType === 'RTMP' || stats?.type === 'rtmpConn') return 'RTMP';
  return 'Standby';
};

const getAny = (source: any, keys: string[]) => {
  for (const key of keys) {
    const value = source?.[key];
    if (value !== undefined && value !== null && value !== '') return value;
  }
  return null;
};

const stripRemotePort = (value?: string | null) => {
  if (!value) return 'Waiting';
  const clean = String(value).trim();
  if (!clean) return 'Waiting';
  if (clean.startsWith('[')) return clean.slice(1).split(']')[0] || clean;
  return clean.includes(':') ? clean.split(':')[0] : clean;
};

const destinationHost = (value?: string | null) => {
  const raw = String(value || '').trim();
  if (!raw) return null;
  try {
    return new URL(raw).hostname || null;
  } catch {
    const withoutProtocol = raw.replace(/^[a-z]+:\/\//i, '');
    return withoutProtocol.split(/[/?#]/)[0]?.split(':')[0] || null;
  }
};

const normalizeProtocol = (stats: StreamStats | null) => {
  if ((stats as any)?.isPlayKastrSource || stats?.sourceMode === 'PLAYKASTR') return 'PlayKaster';
  if ((stats as any)?.isMediaBoxSource || stats?.sourceMode === 'MEDIABOX') return 'MediaBox';
  if (stats?.sourceType === 'SRT' || stats?.type === 'srtConn') return 'SRT';
  if (stats?.sourceType === 'RTMP' || stats?.type === 'rtmpConn') return 'RTMP';
  return '--';
};
const isHlsOutput = (t: Transcoder) => t.protocol === 'hls';
const isPullOutput = (t: Transcoder) => t.deliveryMode === 'PULL' || (t as any).isPullLink === true;
const outputType = (t: Transcoder) =>
  t.protocol === 'hls' ? (t.isHlsSpecial === false ? 'HLS+' : 'HLS') : isPullOutput(t) ? 'Pull' : t.videoCodec === 'copy' ? 'Direct' : 'Remapped';

const hasRuntimeEvidence = (t: Transcoder) => {
  const stats: any = t.status?.stats || {};
  const rawStatus = String(t.status?.status || '').toLowerCase();
  if (['running', 'connected', 'active', 'online'].includes(rawStatus)) return true;
  if (['idle', 'stopped', 'failed', 'error', 'retrying', 'disconnected', 'offline'].includes(rawStatus)) return false;
  if (stats.connected === true || stats.isConnected === true || stats.pulled === true) return true;
  const numericKeys = ['bitrate', 'bitrateKbps', 'currentKbps', 'averageKbps', 'peakKbps', 'lastKbps', 'currentBitrateKbps', 'outputBitrateKbps', 'videoBitrateKbps', 'bytesPerSecond', 'bytesOut', 'totalBytesOut'];
  return numericKeys.some((key) => Number(stats[key] || 0) > 0);
};

const outputRuntimeState = (
  t: Transcoder,
  health?: DestinationHealthEnvelope | null,
  pullStatus?: PullOutputStatusEnvelope['status'] | null,
  hlsSession?: HlsSessionStatusEnvelope | HlsSessionStatusEnvelope['activeSession'] | null,
): OutputState => {
  if (!t.isActive) return 'disabled';
  if (isHlsOutput(t) || isPullOutput(t)) {
    const display = getPullHlsDisplayState(
      t,
      (health || (isHlsOutput(t)
        ? (hlsSession || (t as any).hlsStatus || (t as any).hlsSession || (t as any).activeSession)
        : (pullStatus || (t as any).pullStatus))) as any,
    );
    return display.status;
  }

  if (health?.status === 'connecting' || health?.status === 'connected' || health?.status === 'disconnected' || health?.status === 'disabled') {
    return health.status;
  }

  const raw = String(t.status?.status || '').toLowerCase();
  if (['running', 'connected', 'active', 'online'].includes(raw) || hasRuntimeEvidence(t)) return 'connected';
  if (raw === 'connecting') return 'connecting';
  return 'disconnected';
};

const isConnectedState = (state: OutputState) => state === 'connected';
const isOutputIssue = (
  t: Transcoder,
  health?: DestinationHealthEnvelope | null,
  pullStatus?: PullOutputStatusEnvelope['status'] | null,
  hlsSession?: HlsSessionStatusEnvelope['activeSession'] | null,
) => {
  const state = outputRuntimeState(t, health, pullStatus, hlsSession);
  return state !== 'disabled' && state !== 'connecting' && state !== 'connected';
};

const stateColor = (state: OutputState) => {
  if (state === 'disabled') return 'var(--text-disabled)';
  if (state === 'connecting') return 'var(--warning)';
  if (state === 'connected') return 'var(--success)';
  return 'var(--danger)';
};

const statusLabel = (state: OutputState) => {
  if (state === 'disabled') return 'Disabled';
  if (state === 'connecting') return 'Connecting';
  if (state === 'connected') return 'Connected';
  return 'Disconnected';
};

const normalizeBitrateValue = (value?: string | number | null) => {
  if (value === undefined || value === null || value === '') return null;
  if (typeof value === 'number') return formatBitrate(value);
  const clean = String(value).trim();
  if (!clean) return null;
  if (/mbps|kbps|kbit|m\/s|k\/s/i.test(clean)) return clean;
  const match = clean.match(/(\d+\.?\d*)/);
  return match ? formatBitrate(Number(match[1])) : clean;
};

const getOutputBitrateLabel = (
  out: Transcoder,
  hlsSession?: HlsSessionStatusEnvelope['activeSession'] | null,
) => {
  const stats: any = out.status?.stats || {};
  const hlsStats: any = hlsSession || {};
  const candidates = [
    stats.bitrate,
    stats.bitrateKbps,
    stats.currentKbps,
    stats.averageKbps,
    stats.peakKbps,
    stats.lastKbps,
    stats.currentBitrateKbps,
    stats.outputBitrateKbps,
    stats.videoBitrateKbps,
    stats.bytesPerSecond ? Number(stats.bytesPerSecond) * 8 / 1000 : null,
    (out as any).currentKbps,
    (out as any).averageKbps,
    (out as any).peakKbps,
    (out as any).pullStatus?.currentKbps,
    (out as any).pullStatus?.averageKbps,
    hlsStats.currentKbps,
    hlsStats.averageKbps,
    hlsStats.peakKbps,
    (out as any).hlsSession?.averageKbps,
    (out as any).activeSession?.averageKbps,
    out.videoBitrate,
  ];
  for (const value of candidates) {
    const label = normalizeBitrateValue(value as any);
    if (label && label !== '--') return label;
  }
  return 'Bitrate pending';
};

const OutputStateIcon = ({ state }: { state: OutputState }) => {
  if (state === 'disabled') return <CancelIcon sx={{ fontSize: 17, color: 'var(--text-disabled)' }} />;
  if (state === 'connecting') return <HubIcon sx={{ fontSize: 17, color: 'var(--warning)' }} />;
  if (state === 'connected') return <CheckCircleIcon sx={{ fontSize: 17, color: 'var(--success)' }} />;
  return <CancelIcon sx={{ fontSize: 17, color: 'var(--danger)' }} />;
};

const OutputHealthItem = React.memo(({
  out,
  health,
  pullStatus,
  hlsEnvelope,
  muted,
  onToggleMute,
}: {
  out: Transcoder;
  health: DestinationHealthEnvelope | null;
  pullStatus?: PullOutputStatusEnvelope['status'];
  hlsEnvelope?: HlsSessionStatusEnvelope | null;
  muted: boolean;
  onToggleMute: (outputId: number) => void;
}) => {
  const hlsSession = hlsEnvelope?.activeSession;
  const runtime = isHlsOutput(out) ? (hlsEnvelope || hlsSession) : pullStatus;
  const state = outputRuntimeState(out, health, pullStatus, (hlsEnvelope || hlsSession) as any);
  const issue = isOutputIssue(out, health, pullStatus, (hlsEnvelope || hlsSession) as any);
  const color = stateColor(state);
  const display = isHlsOutput(out) || isPullOutput(out)
    ? getPullHlsDisplayState(out, (isHlsOutput(out) ? (hlsEnvelope || hlsSession) : pullStatus) as any)
    : null;
  const label = display?.healthLabel || statusLabel(state);
  const metrics = getDestinationHealthMetrics(out, runtime as any);
  const ipLabel = metrics.ip || destinationHost(out.destination) || '--';
  const detailLine = [
    outputType(out),
    label,
    `IP ${ipLabel}`,
    metrics.trafficKbps > 0 ? formatBitrate(metrics.trafficKbps) : null,
  ].join(' · ');

  const outputDetailLine = metrics.trafficKbps > 0 ? detailLine : [outputType(out), label, `IP ${ipLabel}`].join(' · ');

  return (
    <Box sx={{ minWidth: 0, px: 0.75, py: 0.65, border: '1px solid', borderColor: color, borderLeft: `3px solid ${color}`, bgcolor: 'var(--bg-card-hover)', display: 'flex', alignItems: 'center', gap: 0.65, boxShadow: issue && !muted ? undefined : 'inset 0 -2px 0 var(--border-subtle)', animation: issue && !muted ? `${pulseAnimation} 1.6s infinite` : 'none' }}>
      <Tooltip title={`${out.destination || 'No destination'} · ${label}`} arrow><Box sx={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}><OutputStateIcon state={state} /></Box></Tooltip>
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography sx={{ fontSize: '0.7rem', fontWeight: 900 }} noWrap>{out.platformDistributor || outputType(out)}</Typography>
        <Typography sx={{ fontSize: '0.62rem', color: issue ? 'error.main' : 'text.secondary' }} noWrap>{outputDetailLine}</Typography>
      </Box>
      {issue && (
        <Tooltip title="Help">
          <IconButton size="small" sx={{ borderRadius: 0, width: 22, height: 22 }}>
            <HelpOutlineIcon sx={{ fontSize: 14 }} />
          </IconButton>
        </Tooltip>
      )}
      {issue && (
        <Tooltip title={muted ? 'Unmute alarm' : 'Mute alarm'}>
          <IconButton size="small" onClick={() => onToggleMute(out.id)} sx={{ borderRadius: 0, width: 22, height: 22 }}>
            {muted ? (
              <VolumeOffIcon sx={{ fontSize: 14 }} />
            ) : (
              <VolumeUpIcon sx={{ fontSize: 14, color: 'error.main' }} />
            )}
          </IconButton>
        </Tooltip>
      )}
    </Box>
  );
});

const OutputHealthRow = ({ outputs }: { outputs: Transcoder[] }) => {
  const {
    pullStatuses: pullStatusMap,
    hlsStatuses: hlsStatusMap,
    outputHealthStatuses,
  } = useRuntimeOutput();
  const {
    mutedAlarmOutputIds: mutedMap,
    toggleOutputAlarmMute,
    clearOutputAlarmMute,
  } = useRuntimeAlarms();
  const previousStateRef = useRef<Record<number, OutputState>>({});
  const activeOutputs = outputs.filter((out) => out.isActive);
  const connectedCount = activeOutputs.filter((t) => isConnectedState(outputRuntimeState(t, outputHealthStatuses[t.id] || (pullStatusMap[t.id] as any)?.destinationHealth || (hlsStatusMap[t.id] as any)?.destinationHealth || null, pullStatusMap[t.id]?.status, hlsStatusMap[t.id]?.activeSession))).length;
  const summary = outputSummaryText(connectedCount, activeOutputs.length);

  useEffect(() => {
    const nextPrevious = { ...previousStateRef.current };
    outputs.forEach((out) => {
      const state = outputRuntimeState(out, outputHealthStatuses[out.id] || (pullStatusMap[out.id] as any)?.destinationHealth || (hlsStatusMap[out.id] as any)?.destinationHealth || null, pullStatusMap[out.id]?.status, hlsStatusMap[out.id]?.activeSession);
      const prev = previousStateRef.current[out.id];
      if ((state === 'connected' || state === 'disabled') && prev !== state) {
        clearOutputAlarmMute(out.id);
      }
      nextPrevious[out.id] = state;
    });
    previousStateRef.current = nextPrevious;
  }, [clearOutputAlarmMute, hlsStatusMap, outputHealthStatuses, outputs, pullStatusMap]);

  if (!outputs.length) {
    return (
      <Box sx={{ ...cardSx, p: 2 }}>
        <Typography sx={{ fontSize: '0.8rem', color: 'text.disabled' }}>No destinations configured yet.</Typography>
      </Box>
    );
  }

  return (
    <Box sx={cardSx}>
      <Box sx={{ px: 1.5, py: 1, borderBottom: '0.5px solid', borderColor: 'divider', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Stack direction="row" spacing={1} alignItems="center"><HubIcon sx={{ fontSize: 16, color: 'text.secondary' }} /><Typography sx={{ fontSize: '0.82rem', fontWeight: 800 }}>Output Health</Typography></Stack>
        <Typography sx={{ fontSize: '0.72rem', color: 'text.secondary' }}>{summary.value} - {summary.helper}</Typography>
      </Box>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))', lg: 'repeat(4, minmax(0, 1fr))' }, gap: 0.75, p: 1 }}>
        {outputs.map((out) => (
          <OutputHealthItem
            key={out.id}
            out={out}
            health={outputHealthStatuses[out.id] || (pullStatusMap[out.id] as any)?.destinationHealth || (hlsStatusMap[out.id] as any)?.destinationHealth || null}
            pullStatus={pullStatusMap[out.id]?.status}
            hlsEnvelope={hlsStatusMap[out.id] || null}
            muted={mutedMap[out.id] === true}
            onToggleMute={toggleOutputAlarmMute}
          />
        ))}
      </Box>
    </Box>
  );
};

export const DashboardTab: React.FC<Props> = ({ stats, analytics, isStreamLive, user, activeChannel }) => {
  const {
    transcoders = [] as Transcoder[],
    pullStatuses: pullStatusMap,
    hlsStatuses: hlsStatusMap,
    outputHealthStatuses,
  } = useRuntimeOutput();
  const [lastUpdated, setLastUpdated] = useState<Date>(new Date());
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [countdownNow, setCountdownNow] = useState(() => Date.now());
  const [bitrateRecovered, setBitrateRecovered] = useState(false);
  const [previewType, setPreviewType] = useState<PreviewType>('webrtc');
  const [telemetryPoints, setTelemetryPoints] = useState<TelemetryPoint[]>([]);
  const [telemetryFilter, setTelemetryFilter] = useState<TelemetryChartFilter>('all');
  const [mediaBoxNowPlaying, setMediaBoxNowPlaying] = useState<string | null>(null);
  const [mediaBoxCurrentAssetId, setMediaBoxCurrentAssetId] = useState<number | null>(null);
  const [mediaBoxAudioCodec, setMediaBoxAudioCodec] = useState<string | null>(null);
  const previousWarningActiveRef = useRef(false);
  const latestTelemetryRef = useRef<TelemetryPoint>({ time: '', bitrateKbps: 0, packetLossPct: 0, latencyMs: 0 });
  const previewPreferenceKey = useMemo(
    () => `streamy.previewType.${activeChannel?.channelHashId || activeChannel?.hashId || activeChannel?.id || 'default'}`,
    [activeChannel?.channelHashId, activeChannel?.hashId, activeChannel?.id],
  );
  const mediaBoxSourceActive = !!(stats as any)?.isMediaBoxSource;

  useEffect(() => setLastUpdated(new Date()), [stats, analytics, transcoders]);

  useEffect(() => {
    try {
      const saved = window.localStorage.getItem(previewPreferenceKey);
      setPreviewType(saved === 'hls' ? 'hls' : 'webrtc');
    } catch {
      setPreviewType('webrtc');
    }
  }, [previewPreferenceKey]);

  const selectPreviewType = (nextType: PreviewType) => {
    setPreviewType(nextType);
    try {
      window.localStorage.setItem(previewPreferenceKey, nextType);
    } catch {}
  };

  useEffect(() => {
    if (!activeChannel || !mediaBoxSourceActive) {
      setMediaBoxNowPlaying(null);
      setMediaBoxCurrentAssetId(null);
      setMediaBoxAudioCodec(null);
      return;
    }

    let cancelled = false;
    let timer: number | undefined;
    const loadCurrentAsset = async () => {
      try {
        const runtime = await getMediaBoxRuntime(activeChannel);
        if (cancelled) return;
        setMediaBoxNowPlaying(runtime.currentAssetLabel || null);
        setMediaBoxCurrentAssetId(runtime.currentAssetId || null);
        if (runtime.currentAssetAudioCodec) {
          setMediaBoxAudioCodec(String(runtime.currentAssetAudioCodec));
        }
      } catch {
        if (!cancelled) {
          setMediaBoxNowPlaying(null);
          setMediaBoxCurrentAssetId(null);
          setMediaBoxAudioCodec(null);
        }
      }
      if (!cancelled) timer = window.setTimeout(loadCurrentAsset, 5000);
    };

    loadCurrentAsset();
    return () => {
      cancelled = true;
      if (timer) window.clearTimeout(timer);
    };
  }, [activeChannel, mediaBoxSourceActive]);

  useEffect(() => {
    if (!activeChannel || !mediaBoxSourceActive || !mediaBoxCurrentAssetId) return;
    let cancelled = false;
    getMediaBoxAssets(activeChannel, { optional: true })
      .then((items) => {
        if (cancelled) return;
        const current = items.find((item) => item.id === mediaBoxCurrentAssetId);
        setMediaBoxAudioCodec(current?.audioCodec || null);
      })
      .catch(() => {
        if (!cancelled) setMediaBoxAudioCodec(null);
      });
    return () => {
      cancelled = true;
    };
  }, [activeChannel, mediaBoxCurrentAssetId, mediaBoxSourceActive]);

  const mediaBoxPreviewLock: PreviewType | null = mediaBoxSourceActive
    ? String(mediaBoxAudioCodec || '').toLowerCase().includes('opus') ? 'webrtc' : 'hls'
    : null;
  const liveWebRtcBlockReason = !mediaBoxSourceActive && isStreamLive
    ? getWebRtcAudioBlockReason(stats?.audioCodec)
    : null;
  const previewLock: PreviewType | null = mediaBoxPreviewLock || (liveWebRtcBlockReason ? 'hls' : null);
  const effectivePreviewType = previewLock || previewType;

  useEffect(() => {
    if (!previewLock || previewType === previewLock) return;
    selectPreviewType(previewLock);
  }, [previewLock, previewType]);

  const profileComplete = !!(user?.firstName && user?.lastName && user?.country);
  const activeOutputCount = useMemo(
    () => transcoders.filter((t) => t.isActive).length,
    [transcoders],
  );
  const connectedOutputs = useMemo(
    () => transcoders.filter((t) => t.isActive && isConnectedState(outputRuntimeState(
      t,
      outputHealthStatuses[t.id] || (pullStatusMap[t.id] as any)?.destinationHealth || (hlsStatusMap[t.id] as any)?.destinationHealth || null,
      pullStatusMap[t.id]?.status,
      hlsStatusMap[t.id]?.activeSession,
    ))).length,
    [hlsStatusMap, outputHealthStatuses, pullStatusMap, transcoders],
  );
  const outputSummary = outputSummaryText(connectedOutputs, activeOutputCount);
  const lastUpdatedLabel = lastUpdated.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false });
  const playerStreamName = activeChannel?.name || user?.streamName || stats?.streamName || 'Preview';
  const playerStreamKeyPin = activeChannel?.streamKeyPin || stats?.streamKeyPin || '';
  const previewActive = isStreamLive || !!(stats as any)?.isPlayKastrSource || !!(stats as any)?.isMediaBoxSource;
  const remoteIp = stripRemotePort((stats as any)?.remoteAddr);
  const receiverLoss = Number((stats as any)?.packetsReceivedLoss ?? 0);
  const receiverTotal = Number((stats as any)?.packetsReceived ?? 0);
  const receiverLossRate = receiverTotal > 0 ? Math.round((receiverLoss / receiverTotal) * 100 * 100) / 100 : 0;
  const rawLatencyMs = getAny(stats as any, ['msRTT', 'rttMs', 'latencyMs']);
  const latencySampleMs = rawLatencyMs === null || Number.isNaN(Number(rawLatencyMs)) ? 0 : Math.max(0, Number(rawLatencyMs));
  const latency = formatMs(getAny(stats as any, ['msRTT', 'rttMs', 'latencyMs']));
  const buffer = formatMs(getAny(stats as any, ['msReceiveTsbPdDelay', 'receiveBufferMs', 'bufferMs']));
  const codecLabel = formatCodecPair(stats?.videoCodec, stats?.audioCodec);
  const codecSourceLabel = currentSourceLabel(stats);
  const bitrateEnforcement = stats?.bitrateEnforcement || analytics?.bitrateEnforcement || null;
  const bitrateCountdown = bitrateEnforcement?.warningActive && bitrateEnforcement?.kickAt
    ? Math.max(0, Math.ceil((new Date(bitrateEnforcement.kickAt).getTime() - countdownNow) / 1000))
    : 0;
  const overviewStatus = bitrateEnforcement?.kickNoticeActive ? 'Attention' : previewActive ? 'On Air' : 'Standby';
  const inputValue = mediaBoxSourceActive ? (truncateMetricText(mediaBoxNowPlaying) || 'MediaBox') : previewActive ? normalizeProtocol(stats) : 'No input';
  const inputHelper = mediaBoxSourceActive ? 'Now Playing' : remoteIp;
  const latencyValue = latency === '--' ? 'N/A' : latency;
  const bufferValue = buffer === '--' ? 'N/A' : buffer;
  const showBitrateGraph = telemetryFilter === 'all' || telemetryFilter === 'bitrate';
  const showLossGraph = telemetryFilter === 'all' || telemetryFilter === 'loss';
  const showLatencyGraph = telemetryFilter === 'all' || telemetryFilter === 'latency';
  const summaryMetrics = [
    { label: 'Input', value: inputValue, helper: inputHelper },
    { label: 'Bitrate', value: formatBitrate(analytics?.bitrateKbps), helper: (stats as any)?.frameRate ? `${(stats as any).frameRate} fps` : '—' },
    { label: 'Uptime', value: formatUptime(analytics?.uptimeSec), helper: `Updated ${lastUpdatedLabel}` },
    { label: 'Outputs', value: outputSummary.value, helper: outputSummary.helper, tone: connectedOutputs > 0 ? 'success' as const : 'default' as const },
    { label: 'Codec', value: codecLabel, helper: codecSourceLabel, tone: codecLabel !== '--' ? 'success' as const : 'default' as const },
    { label: 'Latency', value: latencyValue, helper: 'RTT' },
    { label: 'Buffer', value: bufferValue, helper: 'Receive' },
    { label: 'Loss', value: `${receiverLossRate}%`, helper: `${receiverLoss}/${receiverTotal} packets`, tone: receiverLossRate > 0 ? 'warning' as const : 'success' as const },
  ];
  const previewHelpText = mediaBoxPreviewLock
    ? `This MediaBox uses the ${mediaBoxPreviewLock === 'webrtc' ? 'H264/Opus' : 'H264/AAC'} workflow. ${mediaBoxPreviewLock === 'webrtc' ? 'WebRTC is required; HLS is disabled.' : 'HLS is required; WebRTC is disabled.'}`
    : liveWebRtcBlockReason
      ? liveWebRtcBlockReason
    : 'WebRTC, default, faster, Opus audio. HLS, slower, higher compatibility.';
  const previewTypeControl = (
    <Stack spacing={0.75}>
      <Stack direction="row" alignItems="center" spacing={0.5}>
        <Typography
          sx={{
            fontSize: '0.68rem',
            fontWeight: 900,
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
            color: 'text.disabled',
          }}
        >
          Preview Type
        </Typography>
        <Tooltip title={previewHelpText} arrow>
          <IconButton size="small" sx={{ borderRadius: 0, width: 22, height: 22, color: 'text.secondary' }}>
            <HelpOutlineIcon sx={{ fontSize: 14 }} />
          </IconButton>
        </Tooltip>
      </Stack>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
          border: '1px solid',
          borderColor: 'divider',
          bgcolor: 'background.paper',
          minHeight: 32,
        }}
      >
        {([
          ['webrtc', 'WebRTC'],
          ['hls', 'HLS'],
        ] as const).map(([key, label], index) => {
          const selected = effectivePreviewType === key;
          const disabled = Boolean(previewLock && previewLock !== key);
          return (
            <Button
              key={key}
              size="small"
              disabled={disabled}
              onClick={() => selectPreviewType(key)}
              sx={{
                borderRadius: 0,
                minWidth: 0,
                px: 1,
                color: selected ? 'primary.contrastText' : 'text.primary',
                bgcolor: selected ? 'primary.main' : 'transparent',
                borderLeft: index === 0 ? 0 : '1px solid',
                borderColor: 'divider',
                fontSize: '0.7rem',
                fontWeight: 900,
                textTransform: 'none',
                opacity: disabled ? 0.45 : 1,
                '&:hover': {
                  bgcolor: selected ? 'primary.dark' : 'action.hover',
                },
              }}
            >
              {label}
            </Button>
          );
        })}
      </Box>
    </Stack>
  );

  useEffect(() => {
    latestTelemetryRef.current = {
      time: '',
      bitrateKbps: Math.max(0, Number(analytics?.bitrateKbps || 0)),
      packetLossPct: Math.max(0, receiverLossRate),
      latencyMs: latencySampleMs,
    };
  }, [analytics?.bitrateKbps, latencySampleMs, receiverLossRate]);

  useEffect(() => {
    const sample = () => {
      setTelemetryPoints((current) => [
        ...current.slice(-59),
        {
          ...latestTelemetryRef.current,
          time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false }),
        },
      ]);
    };
    setTelemetryPoints([]);
    sample();
    const timer = window.setInterval(sample, 1000);
    return () => window.clearInterval(timer);
  }, [activeChannel?.id]);

  useEffect(() => {
    if (!bitrateEnforcement?.warningActive) return;
    setCountdownNow(Date.now());
    const timer = window.setInterval(() => setCountdownNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, [bitrateEnforcement?.warningActive, bitrateEnforcement?.kickAt]);

  useEffect(() => {
    const isWarningActive = bitrateEnforcement?.warningActive === true;
    if (previousWarningActiveRef.current && !isWarningActive && !bitrateEnforcement?.kickNoticeActive) {
      setBitrateRecovered(true);
      const timer = window.setTimeout(() => setBitrateRecovered(false), 8000);
      previousWarningActiveRef.current = isWarningActive;
      return () => window.clearTimeout(timer);
    }
    if (isWarningActive || bitrateEnforcement?.kickNoticeActive) {
      setBitrateRecovered(false);
    }
    previousWarningActiveRef.current = isWarningActive;
  }, [bitrateEnforcement?.warningActive, bitrateEnforcement?.kickNoticeActive]);

  if (!activeChannel) {
    return <Box sx={{ p: 4 }}><Typography variant="body2" color="text.disabled">Select a channel to view overview.</Typography></Box>;
  }

  return (
    <Stack spacing={2}>
      {bitrateRecovered && (
        <Alert severity="success" sx={{ borderRadius: 0 }}>
          Ingest bitrate is back within limit. Feed is ok now.
        </Alert>
      )}
      {(activeChannel.billingSuspended || activeChannel.isActive === false) && (
        <Alert severity="error" sx={{ borderRadius: 0 }}>
          This channel is suspended. Inputs, MediaBox, PlayKaster and delivery outputs are disabled. Reason: {activeChannel.suspensionReason || 'Administrator action.'}
        </Alert>
      )}
      {bitrateEnforcement?.kickNoticeActive && (
        <Alert severity="error" sx={{ borderRadius: 0 }}>
          {bitrateEnforcement.kickReason === 'initial_over_limit'
            ? 'Ingest was dropped by the initial bitrate gate. Set your bitrate to a maximum of 2.5 Mbps.'
            : `Ingest was dropped after staying above ${formatBitrate(bitrateEnforcement.limitKbps || 0)} for 30 seconds. Reduce bitrate and reconnect.`}
        </Alert>
      )}
      {bitrateEnforcement?.warningActive && (
        <Alert severity="warning" sx={{ borderRadius: 0 }}>
          Ingest bitrate is {formatBitrate(bitrateEnforcement.currentBitrateKbps)}. Broadcaster limit is {formatBitrate(bitrateEnforcement.limitKbps || 0)}. Reduce bitrate within {bitrateCountdown}s or the feed will be kicked.
        </Alert>
      )}
      <ChannelWorkspaceHeader
        title={activeChannel.name}
        modeLabel={activeChannel.playkastrActive ? 'PlayKastr Channel' : 'Standard Channel'}
        statusLabel={overviewStatus}
        statusTone={bitrateEnforcement?.kickNoticeActive ? 'warning' : previewActive ? 'success' : 'default'}
        sourceLabel={currentSourceLabel(stats)}
        primaryLabel="Current Feed"
        primaryValue={currentSourceLabel(stats)}
        secondaryLabel="Current State"
        secondaryValue={previewActive ? 'Preview connected' : 'Awaiting source'}
        previewTypeControl={previewTypeControl}
        subtitle="Unified channel operations view for ingest, preview, delivery health, and schedule state."
        metrics={summaryMetrics}
        actions={(
          <Tooltip title="Stream details">
            <IconButton onClick={() => setDetailsOpen(true)} sx={{ borderRadius: 0, width: 36, height: 36, border: '1px solid', borderColor: 'primary.main', bgcolor: 'primary.main', color: 'primary.contrastText', '&:hover': { bgcolor: 'primary.dark', borderColor: 'primary.dark' } }}>
              <VpnKeyIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
        )}
        preview={{
          channelId: activeChannel.id,
          channelHashId: activeChannel.channelHashId || activeChannel.hashId || null,
          streamName: playerStreamName,
          streamKeyPin: playerStreamKeyPin,
          isLive: previewActive,
          previewType: effectivePreviewType,
        }}
      />

      {!profileComplete && (
        <Alert severity="warning" sx={{ borderRadius: 0 }}>
          Complete profile identity details in Settings before your next live event.
        </Alert>
      )}

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', xl: 'minmax(0, 1.18fr) minmax(320px, 0.82fr)' }, gap: 2 }}>
        <Stack spacing={2} sx={{ minWidth: 0 }}>
          <OutputHealthRow outputs={transcoders} />

          <Box sx={{ ...cardSx, p: 1.5 }}>
            <Typography sx={{ fontSize: '0.84rem', fontWeight: 900, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'text.secondary', mb: 1.2 }}>
              Delivery Snapshot
            </Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, minmax(0, 1fr))' }, gap: 1.2 }}>
              <Box sx={{ border: '1px solid', borderColor: 'divider', bgcolor: 'background.default', p: 1.2 }}>
                <Typography sx={labelSx}>Connected Outputs</Typography>
                <Typography sx={{ mt: 0.55, fontSize: '1rem', fontWeight: 900, color: 'text.primary' }}>
                  {connectedOutputs}/{transcoders.length}
                </Typography>
                <Typography sx={{ mt: 0.25, fontSize: '0.72rem', color: 'text.secondary' }}>
                  Direct and managed paths
                </Typography>
              </Box>
              <Box sx={{ border: '1px solid', borderColor: 'divider', bgcolor: 'background.default', p: 1.2 }}>
                <Typography sx={labelSx}>Destination Mode</Typography>
                <Typography sx={{ mt: 0.55, fontSize: '1rem', fontWeight: 900, color: 'text.primary' }}>
                  {activeChannel.playkastrActive ? 'PlayKastr-backed' : 'Live channel'}
                </Typography>
                <Typography sx={{ mt: 0.25, fontSize: '0.72rem', color: 'text.secondary' }}>
                  Shared Streamy delivery fabric
                </Typography>
              </Box>
              <Box sx={{ border: '1px solid', borderColor: 'divider', bgcolor: 'background.default', p: 1.2 }}>
                <Typography sx={labelSx}>Last Refresh</Typography>
                <Typography sx={{ mt: 0.55, fontSize: '1rem', fontWeight: 900, color: 'text.primary' }}>
                  {lastUpdatedLabel}
                </Typography>
                <Typography sx={{ mt: 0.25, fontSize: '0.72rem', color: 'text.secondary' }}>
                  Overview telemetry snapshot
                </Typography>
              </Box>
            </Box>
          </Box>
        </Stack>

        <Stack spacing={2} sx={{ minWidth: 0 }}>
          <Box sx={{ ...cardSx, p: 1.5 }}>
            <Stack direction="row" spacing={1.25} alignItems="center" justifyContent="space-between" sx={{ mb: 1.2 }}>
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontSize: '0.84rem', fontWeight: 900, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'text.secondary' }}>
                  Bandwidth, Packet Loss and Latency
                </Typography>
                <Stack direction="row" spacing={1.2} flexWrap="wrap" useFlexGap sx={{ mt: 0.35 }}>
                  <Typography sx={{ fontSize: '0.68rem', color: 'primary.main', fontWeight: 800 }}>{formatBitrate(analytics?.bitrateKbps)}</Typography>
                  <Typography sx={{ fontSize: '0.68rem', color: receiverLossRate > 0 ? 'warning.main' : 'success.main', fontWeight: 800 }}>{receiverLossRate}% loss</Typography>
                  <Typography sx={{ fontSize: '0.68rem', color: 'secondary.main', fontWeight: 800 }}>{latencyValue}</Typography>
                </Stack>
              </Box>
              <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap justifyContent="flex-end">
                {[
                  { key: 'all', label: 'All' },
                  { key: 'bitrate', label: 'Bitrate' },
                  { key: 'loss', label: 'Packet Loss' },
                  { key: 'latency', label: 'Latency' },
                ].map((item) => (
                  <Chip
                    key={item.key}
                    label={item.label}
                    size="small"
                    clickable
                    color={telemetryFilter === item.key ? 'primary' : 'default'}
                    variant={telemetryFilter === item.key ? 'filled' : 'outlined'}
                    onClick={() => setTelemetryFilter(item.key as TelemetryChartFilter)}
                    sx={{ borderRadius: 0, height: 22, fontSize: '0.62rem', fontWeight: 800 }}
                  />
                ))}
              </Stack>
            </Stack>
            <Box sx={{ width: '100%', minWidth: 1, height: 260, minHeight: 260 }}>
              <ResponsiveContainer width="100%" height="100%" minWidth={1} minHeight={1}>
                <ComposedChart data={telemetryPoints} margin={{ top: 8, right: 4, left: -24, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--border-subtle)" vertical={false} />
                  <XAxis dataKey="time" tick={{ fill: 'var(--text-secondary)', fontSize: 10 }} axisLine={false} tickLine={false} minTickGap={24} />
                  {showBitrateGraph && <YAxis yAxisId="bitrate" tick={{ fill: 'var(--text-secondary)', fontSize: 10 }} axisLine={false} tickLine={false} />}
                  {showLossGraph && <YAxis yAxisId="loss" orientation="right" domain={[0, 'auto']} tick={{ fill: 'var(--text-secondary)', fontSize: 10 }} axisLine={false} tickLine={false} unit="%" width={36} />}
                  {showLatencyGraph && <YAxis yAxisId="latency" orientation="right" domain={[0, 'auto']} tick={{ fill: 'var(--text-secondary)', fontSize: 10 }} axisLine={false} tickLine={false} unit="ms" width={44} />}
                  <ChartTooltip contentStyle={{ borderRadius: 0, border: '1px solid var(--border)', background: 'var(--bg-card)' }} />
                  {showBitrateGraph && <Line yAxisId="bitrate" type="monotone" dataKey="bitrateKbps" name="Bandwidth Kbps" stroke="var(--primary)" strokeWidth={2} dot={false} isAnimationActive={false} />}
                  {showLossGraph && <Line yAxisId="loss" type="monotone" dataKey="packetLossPct" name="Packet loss %" stroke="var(--warning)" strokeWidth={2} dot={false} isAnimationActive={false} />}
                  {showLatencyGraph && <Line yAxisId="latency" type="monotone" dataKey="latencyMs" name="Latency ms" stroke="var(--secondary)" strokeWidth={2} dot={false} isAnimationActive={false} />}
                </ComposedChart>
              </ResponsiveContainer>
            </Box>
          </Box>

        </Stack>
      </Box>

      <Dialog open={detailsOpen} onClose={() => setDetailsOpen(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 0, m: { xs: 1, sm: 3 }, maxWidth: { sm: 520 } } }}>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pr: 1, py: 1.25 }}>
          <Typography fontWeight={900}>Stream Details</Typography>
          <IconButton onClick={() => setDetailsOpen(false)} sx={{ borderRadius: 0 }}><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: { xs: 1, sm: 1.5 } }}><StreamDetailsCard stats={stats} channel={activeChannel} /></DialogContent>
      </Dialog>
    </Stack>
  );
};
